import { base64ToBlob } from '@shared/util/base64ToBlob';
import { getFileMetaText, type FileMetaSource } from '@shared/util/fileMeta';

import downloadIcon from '@assets/icons/ai-chat/download.svg';
import fileIcon from '@assets/icons/ai-chat/file.svg';
import paperclipIcon from '@assets/icons/ai-chat/paperclip.svg';

import type {
    ShadcnUploadFileVM,
    ShadcnUploadMainProps,
} from '../../ShadcnUploadFlowClient';

import { Trash2 } from 'lucide-react';
import {
    useState,
    type RefObject,
    type ChangeEventHandler,
    type DragEventHandler,
} from 'react';
import { useTranslation } from 'react-i18next';

type Props<T extends ShadcnUploadFileVM> = {
    files: T[];
    disabled?: boolean;
    readOnly?: boolean;
    canUpload: boolean;
    accept?: string;
    inputRef: RefObject<HTMLInputElement>;
    onInputChange: ChangeEventHandler<HTMLInputElement>;
    onClick: () => void;
    onDragOver: DragEventHandler;
    onDragLeave: DragEventHandler;
    onDrop: DragEventHandler;
    onDownload?: ShadcnUploadMainProps<T>['onDownload'];
    onDelete?: (file: T) => Promise<void>;
};

export function ProcessFieldFileUploadAiChat<T extends ShadcnUploadFileVM>({
    files,
    disabled,
    readOnly,
    canUpload,
    accept,
    inputRef,
    onInputChange,
    onClick,
    onDragOver,
    onDragLeave,
    onDrop,
    onDownload,
    onDelete,
}: Props<T>) {
    const { t } = useTranslation();
    const [downloading, setDownloading] = useState<string | null>(null);
    const download = async (file: T) => {
        setDownloading(file.id);
        try {
            const data = await onDownload?.(file);
            if (!data?.content) return;
            const blob = base64ToBlob(data.content, data.extension);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = data.name || file.name;
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } finally {
            setDownloading(null);
        }
    };
    const actionClass =
        'flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent text-(--color-text-grey) hover:bg-black/5 focus-visible:outline-2 focus-visible:outline-(--color-brand-green) disabled:cursor-default disabled:opacity-50 dark:hover:bg-white/5';

    return (
        <div className='flex w-full flex-col gap-2.5'>
            {!readOnly && canUpload && (
                <button
                    type='button'
                    disabled={disabled}
                    className='relative flex h-12 w-full cursor-pointer items-center gap-3 rounded-xl border-0 bg-(--color-ai-file-upload-bg) px-4 text-left text-sm leading-5 font-medium text-(--color-card-text) focus-visible:outline-2 focus-visible:outline-(--color-brand-green) disabled:cursor-default disabled:opacity-50'
                    onClick={onClick}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                >
                    <img src={paperclipIcon} alt='' aria-hidden />
                    {t('translation:selectFile')}
                    <input
                        ref={inputRef}
                        type='file'
                        accept={accept}
                        disabled={disabled}
                        tabIndex={-1}
                        className='hidden'
                        onClick={(event) => event.stopPropagation()}
                        onChange={onInputChange}
                    />
                </button>
            )}
            {files.map((file) => {
                const extension =
                    file.name.split('.').pop()?.toUpperCase() ?? '';
                const meta = getFileMetaText(
                    file as T & FileMetaSource,
                ).replace(' | ', ' . ');
                return (
                    <div
                        key={file.id}
                        className='flex min-h-[57px] w-full items-center gap-4 rounded-xl bg-(--color-ai-file-card-bg) pr-1.5 pl-4'
                    >
                        <div className='flex size-[35px] shrink-0 items-center justify-center rounded bg-white'>
                            <div className='relative flex size-[23px] items-center justify-center'>
                                <img src={fileIcon} alt='' aria-hidden />
                                <span className='absolute right-[4.17%] bottom-[16.67%] left-[4.17%] flex h-[41.67%] items-center justify-center rounded-[1px] bg-(--color-danger) text-[6.57px] leading-none font-semibold text-white'>
                                    {extension.slice(0, 5)}
                                </span>
                            </div>
                        </div>
                        <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                            <span
                                className='truncate text-sm leading-5 font-medium text-(--color-card-text)'
                                title={file.name}
                            >
                                {file.name}
                            </span>
                            {meta && (
                                <span className='truncate text-xs text-(--color-text-grey)'>
                                    {meta}
                                </span>
                            )}
                        </div>
                        <div className='flex shrink-0'>
                            {onDownload && (
                                <button
                                    type='button'
                                    className={actionClass}
                                    disabled={downloading !== null}
                                    aria-label={`${t('translation:download')} ${file.name}`}
                                    onClick={() => void download(file)}
                                >
                                    <img
                                        src={downloadIcon}
                                        alt=''
                                        aria-hidden
                                    />
                                </button>
                            )}
                            {!readOnly && onDelete && (
                                <button
                                    type='button'
                                    className={actionClass}
                                    disabled={disabled}
                                    aria-label={`${t('translation:delete')} ${file.name}`}
                                    onClick={() => void onDelete(file)}
                                >
                                    <Trash2 size={16} aria-hidden />
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
            {readOnly && !files.length && (
                <div className='rounded-xl bg-(--color-ai-file-upload-bg) px-4 py-3 text-sm text-(--color-text-grey)'>
                    {t('translation:noFile')}
                </div>
            )}
        </div>
    );
}
